import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import "./AdminPanel.css";
import { formatDuration } from "./formatDuration";
import publicOrigin from "./publicOrigin";

const SECTIONS = [
  { key: "dashboard", label: "Dashboard", icon: "📊" },
  { key: "studios", label: "Studios", icon: "🏢" },
  { key: "banners", label: "Ad Banners", icon: "🖼️" },
  { key: "wallet", label: "Wallet", icon: "💰" },
  { key: "plans", label: "Subscription Plans", icon: "🎟️" },
  { key: "hero", label: "Hero Section", icon: "🏠" },
  { key: "brands", label: "Brand Logos", icon: "🏷️" },
  { key: "features", label: "Features", icon: "⚡" },
  { key: "album_review", label: "Album Review", icon: "📕" },
  { key: "services", label: "Services", icon: "📸" },
  { key: "steps", label: "How It Works", icon: "🔄" },
  { key: "pricing", label: "Pricing", icon: "💰" },
  { key: "testimonials", label: "Testimonials", icon: "💬" },
  { key: "faq", label: "FAQ", icon: "❓" },
  { key: "cta", label: "CTA Section", icon: "🚀" },
  { key: "ai", label: "AI Settings", icon: "🤖" },
  { key: "photo_frames", label: "Photo Frames", icon: "🖼️" },
  { key: "frame_payouts", label: "Frame Payouts", icon: "💵" },
  { key: "payment_links", label: "Payment Links", icon: "🔗" },
  { key: "exchange_rates", label: "Exchange Rates", icon: "💱" },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [content, setContent] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [editingUser, setEditingUser] = useState(null);

  // Wallet state
  const [walletUsers, setWalletUsers] = useState([]);
  const [walletSearch, setWalletSearch] = useState("");
  const [creditModal, setCreditModal] = useState(null);
  const [creditForm, setCreditForm] = useState({ amount: "", payment_method: "", transaction_ref: "", description: "" });
  const [crediting, setCrediting] = useState(false);
  const [walletTxModal, setWalletTxModal] = useState(null);
  const [walletTxList, setWalletTxList] = useState([]);
  const [eventCost, setEventCost] = useState("699");
  const [savingCost, setSavingCost] = useState(false);
  const [qrPublicMode, setQrPublicMode] = useState("live");
  const [savingQrMode, setSavingQrMode] = useState(false);

  // Banner state
  const [bannerEvents, setBannerEvents] = useState([]);
  const [selectedBannerEventId, setSelectedBannerEventId] = useState(null);
  const [banners, setBanners] = useState([]);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerEventSearch, setBannerEventSearch] = useState("");

  // Exchange Rates state
  const [exchangeRates, setExchangeRates] = useState([]);
  const [exchangeRatesLoading, setExchangeRatesLoading] = useState(false);
  const [savingRate, setSavingRate] = useState(null);

  // AI Settings state
  const [aiModel, setAiModel] = useState("gemini-2.5-flash");
  const [savingAiModel, setSavingAiModel] = useState(false);

  // Subscription plans state
  const [plans, setPlans] = useState([]);
  const [planEdits, setPlanEdits] = useState({});
  const [savingPlan, setSavingPlan] = useState(null);

  // Assign-subscription modal state
  const [subModal, setSubModal] = useState(null);
  const [subForm, setSubForm] = useState({ plan_slug: "", duration_days: "", note: "" });
  const [assigningSub, setAssigningSub] = useState(false);

  // Photo frame catalog + markup state
  const [frameMarkup, setFrameMarkup] = useState("15");
  const [savingFrameMarkup, setSavingFrameMarkup] = useState(false);
  const [frameDimensions, setFrameDimensions] = useState([]);
  const [dimensionEdits, setDimensionEdits] = useState({});
  const [savingDimension, setSavingDimension] = useState(null);
  const [newDimension, setNewDimension] = useState({ label: "", width_inches: "", height_inches: "" });
  const [addingDimension, setAddingDimension] = useState(false);

  // Frame orders (admin) state
  const [frameOrderItems, setFrameOrderItems] = useState([]);
  const [frameOrderSummary, setFrameOrderSummary] = useState({ totalOwed: 0, byStudio: [] });
  const [frameOrderStatusFilter, setFrameOrderStatusFilter] = useState("");
  const [frameOrderPayoutFilter, setFrameOrderPayoutFilter] = useState("");
  const [savingPayoutStatusId, setSavingPayoutStatusId] = useState(null);
  const [deliveringOrderId, setDeliveringOrderId] = useState(null);

  // Payment links state
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [paymentLinkForm, setPaymentLinkForm] = useState({ amount: "", description: "", note: "" });
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);

  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const handleImpersonate = (user) => {
    // Save admin credentials so we can switch back
    localStorage.setItem("admin_token", localStorage.getItem("token"));
    localStorage.setItem("admin_userId", localStorage.getItem("userId"));
    localStorage.setItem("admin_userEmail", localStorage.getItem("userEmail"));
    localStorage.setItem("admin_userRole", localStorage.getItem("userRole"));
    // Switch to the studio user
    localStorage.setItem("userId", user.id);
    localStorage.setItem("userEmail", user.email);
    localStorage.setItem("userRole", user.role || "user");
    if (user.studio_slug) localStorage.setItem("studioSlug", user.studio_slug);
    // Navigate to dashboard as that user
    navigate("/dashboard");
  };

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const loadContent = useCallback(async () => {
    try {
      const [contentRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/landing-content`, { headers }),
        fetch(`${API_BASE_URL}/api/admin/stats`, { headers }),
      ]);
      if (contentRes.status === 403 || contentRes.status === 401) {
        navigate("/dashboard");
        return;
      }
      const contentData = await contentRes.json();
      const statsData = await statsRes.json();
      // Convert array to grouped object for easier editing
      const grouped = {};
      for (const row of contentData) {
        if (!grouped[row.section]) grouped[row.section] = {};
        grouped[row.section][row.content_key] = row.content_value;
      }
      setContent(grouped);
      setStats(statsData);
    } catch (err) {
      showToast("Failed to load content", true);
    } finally {
      setLoading(false);
    }
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error("Failed to load users");
    }
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const loadWallets = useCallback(async () => {
    try {
      const [walletsRes, settingsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/wallets`, { headers }),
        fetch(`${API_BASE_URL}/api/admin/settings`, { headers }),
      ]);
      if (walletsRes.ok) setWalletUsers(await walletsRes.json());
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        setEventCost(s.event_creation_cost || "699");
        setQrPublicMode(s.qr_public_mode === "whatsapp" ? "whatsapp" : "live");
        if (s.gemini_model) setAiModel(s.gemini_model);
        setFrameMarkup(s.platform_markup_percentage || "15");
      }
    } catch (err) {
      console.error("Failed to load wallets");
    }
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Verify admin access
    fetch(`${API_BASE_URL}/api/admin/verify`, { headers })
      .then((r) => {
        if (!r.ok) { navigate("/dashboard"); return; }
        loadContent();
        loadUsers();
        loadWallets();
      })
      .catch(() => navigate("/dashboard"));
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (section, key, value) => {
    setContent((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
    setHasChanges(true);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const items = [];
      for (const [section, fields] of Object.entries(content)) {
        for (const [key, value] of Object.entries(fields)) {
          items.push({ section, content_key: key, content_value: value, sort_order: 0 });
        }
      }
      const res = await fetch(`${API_BASE_URL}/api/admin/landing-content/bulk`, {
        method: "POST",
        headers,
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error("Save failed");
      setHasChanges(false);
      showToast("All changes saved successfully!");
    } catch (err) {
      showToast("Failed to save changes", true);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  // Banner functions
  const fetchBannerEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/events`, { headers });
      const data = await res.json();
      setBannerEvents(data.events || []);
    } catch {}
  }, []);

  const fetchBannersForEvent = async (eventId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/banners`);
      const data = await res.json();
      setBanners(data.banners || []);
    } catch {}
  };

  const handleSelectBannerEvent = (eventId) => {
    setSelectedBannerEventId(eventId);
    fetchBannersForEvent(eventId);
  };

  const handleBannerUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedBannerEventId) return;
    setBannerUploading(true);
    try {
      for (const file of files) {
        const imageData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        await fetch(`${API_BASE_URL}/api/events/${selectedBannerEventId}/banners`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageData }),
        });
      }
      await fetchBannersForEvent(selectedBannerEventId);
      await fetchBannerEvents();
      showToast(`${files.length} banner${files.length > 1 ? "s" : ""} uploaded!`);
    } catch {
      showToast("Failed to upload banner", true);
    } finally {
      setBannerUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!selectedBannerEventId) return;
    try {
      await fetch(`${API_BASE_URL}/api/events/${selectedBannerEventId}/banners/${bannerId}`, {
        method: "DELETE"
      });
      await fetchBannersForEvent(selectedBannerEventId);
      await fetchBannerEvents();
      showToast("Banner deleted");
    } catch {}
  };

  useEffect(() => {
    if (activeSection === "banners") fetchBannerEvents();
  }, [activeSection, fetchBannerEvents]);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/subscription-plans`, { headers });
      const data = await res.json();
      setPlans(data);
      const edits = {};
      for (const p of data) {
        const storageGb = p.max_storage_bytes ? (p.max_storage_bytes / 1073741824).toFixed(0) : "";
        edits[p.slug] = {
          price: p.price,
          price_yearly: p.price_yearly === null ? "" : p.price_yearly,
          max_events: p.max_events === null ? "" : p.max_events,
          max_photos_per_event: p.max_photos_per_event === null ? "" : p.max_photos_per_event,
          no_compression: p.no_compression,
          duration_days: p.duration_days === null ? "" : p.duration_days,
          max_storage_gb: storageGb,
        };
      }
      setPlanEdits(edits);
    } catch {
      showToast("Failed to load subscription plans", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeSection === "plans" || activeSection === "wallet") fetchPlans();
  }, [activeSection, fetchPlans]);

  const updatePlanEdit = (slug, field, value) => {
    setPlanEdits((prev) => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }));
  };

  const savePlan = async (slug) => {
    const edit = planEdits[slug];
    setSavingPlan(slug);
    try {
      // max_storage_gb input is in GB; convert to bytes before sending
      const maxStorageGb = edit.max_storage_gb === "" || edit.max_storage_gb === undefined ? null : parseFloat(edit.max_storage_gb);
      const maxStorageBytes = maxStorageGb !== null && !isNaN(maxStorageGb) ? Math.round(maxStorageGb * 1073741824) : null;
      const res = await fetch(`${API_BASE_URL}/api/admin/subscription-plans/${slug}`, {
        method: "PUT", headers,
        body: JSON.stringify({
          price: edit.price,
          price_yearly: edit.price_yearly === "" ? null : edit.price_yearly,
          max_events: edit.max_events === "" ? null : edit.max_events,
          max_photos_per_event: edit.max_photos_per_event === "" ? null : edit.max_photos_per_event,
          no_compression: edit.no_compression,
          duration_days: edit.duration_days === "" ? null : edit.duration_days,
          max_storage_bytes: maxStorageBytes,
        }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setPlans((prev) => prev.map((p) => (p.slug === slug ? updated : p)));
      showToast(`${updated.name} plan updated`);
    } catch {
      showToast("Failed to update plan", true);
    } finally {
      setSavingPlan(null);
    }
  };

  const fetchFrameDimensions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/photo-frame-dimensions`, { headers });
      const data = await res.json();
      setFrameDimensions(data);
      const edits = {};
      for (const d of data) {
        edits[d.id] = { label: d.label, width_inches: d.width_inches, height_inches: d.height_inches, display_order: d.display_order, active: d.active };
      }
      setDimensionEdits(edits);
    } catch {
      showToast("Failed to load frame dimensions", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeSection === "photo_frames") fetchFrameDimensions();
  }, [activeSection, fetchFrameDimensions]);

  const fetchFrameOrders = useCallback(async (status, payoutStatus) => {
    try {
      const qs = new URLSearchParams();
      if (status) qs.set("status", status);
      if (payoutStatus) qs.set("payoutStatus", payoutStatus);
      const res = await fetch(`${API_BASE_URL}/api/admin/photo-frame-orders?${qs.toString()}`, { headers });
      const data = await res.json();
      setFrameOrderItems(data.items || []);
      setFrameOrderSummary(data.summary || { totalOwed: 0, byStudio: [] });
    } catch {
      showToast("Failed to load frame orders", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeSection === "frame_payouts") fetchFrameOrders(frameOrderStatusFilter, frameOrderPayoutFilter);
  }, [activeSection, frameOrderStatusFilter, frameOrderPayoutFilter, fetchFrameOrders]);

  const fetchPaymentLinks = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/payment-links`, { headers });
      const data = await res.json();
      setPaymentLinks(data);
    } catch {
      showToast("Failed to load payment links", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeSection === "payment_links") fetchPaymentLinks();
  }, [activeSection, fetchPaymentLinks]);

  const updatePayoutStatus = async (orderId, status) => {
    setSavingPayoutStatusId(orderId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/photo-frame-orders/${orderId}/payout-status`, {
        method: "PATCH", headers, body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      showToast("Payout status updated");
      fetchFrameOrders(frameOrderStatusFilter, frameOrderPayoutFilter);
    } catch {
      showToast("Failed to update payout status", true);
    } finally {
      setSavingPayoutStatusId(null);
    }
  };

  const deliverFrameOrder = async (order) => {
    if (!window.confirm(`Mark #${order.order_number} as delivered?`)) return;
    setDeliveringOrderId(order.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/photo-frame-orders/${order.id}/deliver`, { method: "PATCH", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to mark delivered");
      showToast("Order marked as delivered");
      fetchFrameOrders(frameOrderStatusFilter, frameOrderPayoutFilter);
    } catch (err) {
      showToast(err.message || "Failed to mark delivered", true);
    } finally {
      setDeliveringOrderId(null);
    }
  };

  const goToStudioProfile = (studioEmail) => {
    setActiveSection("studios");
    setUserSearch(studioEmail);
  };

  const updateDimensionEdit = (id, field, value) => {
    setDimensionEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveFrameMarkup = async () => {
    setSavingFrameMarkup(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        method: "PUT", headers,
        body: JSON.stringify({ key: "platform_markup_percentage", value: frameMarkup }),
      });
      if (!res.ok) throw new Error();
      showToast(`Platform markup updated to ${frameMarkup}%`);
    } catch {
      showToast("Failed to update markup", true);
    } finally {
      setSavingFrameMarkup(false);
    }
  };

  const saveDimension = async (id) => {
    const edit = dimensionEdits[id];
    setSavingDimension(id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/photo-frame-dimensions/${id}`, {
        method: "PUT", headers,
        body: JSON.stringify(edit),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setFrameDimensions((prev) => prev.map((d) => (d.id === id ? updated : d)));
      showToast(`${updated.label} updated`);
    } catch {
      showToast("Failed to update dimension", true);
    } finally {
      setSavingDimension(null);
    }
  };

  const addDimension = async () => {
    if (!newDimension.label || !newDimension.width_inches || !newDimension.height_inches) {
      showToast("Label, width, and height are required", true);
      return;
    }
    setAddingDimension(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/photo-frame-dimensions`, {
        method: "POST", headers,
        body: JSON.stringify({ ...newDimension, display_order: frameDimensions.length + 1 }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to add dimension");
      }
      setNewDimension({ label: "", width_inches: "", height_inches: "" });
      showToast("Dimension added");
      fetchFrameDimensions();
    } catch (err) {
      showToast(err.message || "Failed to add dimension", true);
    } finally {
      setAddingDimension(false);
    }
  };

  const deleteDimension = async (id) => {
    if (!window.confirm("Delete this dimension? This only works if no studio has priced it yet.")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/photo-frame-dimensions/${id}`, { method: "DELETE", headers });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to delete dimension");
      }
      showToast("Dimension deleted");
      fetchFrameDimensions();
    } catch (err) {
      showToast(err.message || "Failed to delete dimension", true);
    }
  };

  if (loading) {
    return <div className="admin"><div className="admin-loading">Loading admin panel...</div></div>;
  }

  const Field = ({ section, field, label, multiline }) => (
    <div className="admin-form-group">
      <label>{label}</label>
      {multiline ? (
        <textarea
          value={(content[section] && content[section][field]) || ""}
          onChange={(e) => updateField(section, field, e.target.value)}
          placeholder={label}
        />
      ) : (
        <input
          type="text"
          value={(content[section] && content[section][field]) || ""}
          onChange={(e) => updateField(section, field, e.target.value)}
          placeholder={label}
        />
      )}
    </div>
  );

  const renderDashboard = () => (
    <>
      <div className="admin-stats">
        <div className="admin-stat-card">
          <div className="admin-stat-card__label">Total Users</div>
          <div className="admin-stat-card__value">{stats?.total_users || 0}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__label">Total Events</div>
          <div className="admin-stat-card__value">{stats?.total_events || 0}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__label">Total Images</div>
          <div className="admin-stat-card__value">{stats?.total_images || 0}</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-card__label">Shared Links</div>
          <div className="admin-stat-card__value">{stats?.total_shares || 0}</div>
        </div>
      </div>
      <div className="admin-section">
        <div className="admin-section__header">
          <h3 className="admin-section__title">Quick Guide</h3>
        </div>
        <div className="admin-section__body">
          <p style={{ color: "#6b7280", lineHeight: 1.7 }}>
            Use the sidebar to navigate between landing page sections. Edit the text content
            for each section and click <strong>Save All Changes</strong> to publish.
            Content will appear on the live site immediately after saving.
          </p>
        </div>
      </div>
    </>
  );

  const renderHero = () => (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3 className="admin-section__title">Hero Section</h3>
      </div>
      <div className="admin-section__body">
        <Field section="hero" field="badge" label="Badge Text" />
        <Field section="hero" field="title_line1" label="Title Line 1" />
        <Field section="hero" field="title_gradient" label="Title Gradient Text" />
        <Field section="hero" field="subtitle" label="Subtitle" multiline />
        <Field section="hero" field="cta_primary" label="Primary CTA Button Text" />
        <Field section="hero" field="cta_secondary" label="Secondary CTA Button Text" />
        <div className="admin-card-editor__row">
          <Field section="hero" field="stat1_num" label="Stat 1 Number" />
          <Field section="hero" field="stat1_label" label="Stat 1 Label" />
        </div>
        <div className="admin-card-editor__row">
          <Field section="hero" field="stat2_num" label="Stat 2 Number" />
          <Field section="hero" field="stat2_label" label="Stat 2 Label" />
        </div>
        <div className="admin-card-editor__row">
          <Field section="hero" field="stat3_num" label="Stat 3 Number" />
          <Field section="hero" field="stat3_label" label="Stat 3 Label" />
        </div>
      </div>
    </div>
  );

  const renderBrands = () => (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3 className="admin-section__title">Brand Logos</h3>
      </div>
      <div className="admin-section__body">
        <Field section="brands" field="label" label="Section Label" />
        <Field section="brands" field="items" label="Brand Names (comma separated)" />
      </div>
    </div>
  );

  const renderCardSection = (sectionKey, title, fields, count) => {
    const cards = [];
    for (let i = 1; i <= count; i++) {
      cards.push(
        <div key={i} className="admin-card-editor">
          <div className="admin-card-editor__header">
            <span className="admin-card-editor__num">#{i}</span>
          </div>
          {fields.map((f) => (
            <Field
              key={f.key}
              section={sectionKey}
              field={`${f.key}_${i}`}
              label={f.label}
              multiline={f.multiline}
            />
          ))}
        </div>
      );
    }
    return (
      <div className="admin-section">
        <div className="admin-section__header">
          <h3 className="admin-section__title">{title}</h3>
        </div>
        <div className="admin-section__body">
          <Field section={sectionKey} field="section_tag" label="Section Tag" />
          <Field section={sectionKey} field="section_title" label="Section Title" />
          <Field section={sectionKey} field="section_desc" label="Section Description" multiline />
          <div className="admin-cards">{cards}</div>
        </div>
      </div>
    );
  };

  const renderFeatures = () =>
    renderCardSection("features", "Features (6 Cards)", [
      { key: "title", label: "Title" },
      { key: "desc", label: "Description", multiline: true },
    ], 6);

  const renderServices = () =>
    renderCardSection("services", "Services (6 Cards)", [
      { key: "emoji", label: "Emoji" },
      { key: "title", label: "Title" },
      { key: "desc", label: "Description", multiline: true },
    ], 6);

  const renderSteps = () => (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3 className="admin-section__title">How It Works (4 Steps)</h3>
      </div>
      <div className="admin-section__body">
        <Field section="steps" field="section_tag" label="Section Tag" />
        <Field section="steps" field="section_title" label="Section Title" />
        <div className="admin-cards">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="admin-card-editor">
              <div className="admin-card-editor__header">
                <span className="admin-card-editor__num">Step {i}</span>
              </div>
              <Field section="steps" field={`title_${i}`} label="Title" />
              <Field section="steps" field={`desc_${i}`} label="Description" multiline />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAlbumReview = () => (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3 className="admin-section__title">Album Review (PDF Proofing)</h3>
      </div>
      <div className="admin-section__body">
        <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
          The album spread mockup beside these points is illustrative and not editable here.
        </p>
        <Field section="album_review" field="section_tag" label="Section Tag" />
        <Field section="album_review" field="section_title" label="Section Title" />
        <Field section="album_review" field="section_desc" label="Section Description" multiline />
        <div className="admin-cards">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="admin-card-editor">
              <div className="admin-card-editor__header">
                <span className="admin-card-editor__num">Point {i}</span>
              </div>
              <Field section="album_review" field={`title_${i}`} label="Title" />
              <Field section="album_review" field={`desc_${i}`} label="Description" multiline />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPricing = () => (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3 className="admin-section__title">Pricing Plans</h3>
      </div>
      <div className="admin-section__body">
        <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
          This only edits the section heading shown above the pricing cards. Plan names, prices, limits, and
          compression behavior come live from the <strong>Subscription Plans</strong> tab.
        </p>
        <Field section="pricing" field="section_tag" label="Section Tag" />
        <Field section="pricing" field="section_title" label="Section Title" />
        <Field section="pricing" field="section_desc" label="Section Description" multiline />
      </div>
    </div>
  );

  const renderTestimonials = () =>
    renderCardSection("testimonials", "Testimonials (6 Cards)", [
      { key: "name", label: "Name" },
      { key: "role", label: "Role / Location" },
      { key: "quote", label: "Quote", multiline: true },
    ], 6);

  const renderFaq = () =>
    renderCardSection("faq", "FAQ (6 Items)", [
      { key: "question", label: "Question" },
      { key: "answer", label: "Answer", multiline: true },
    ], 6);

  const renderCta = () => (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3 className="admin-section__title">CTA Section</h3>
      </div>
      <div className="admin-section__body">
        <Field section="cta" field="title" label="Title" />
        <Field section="cta" field="description" label="Description" multiline />
        <Field section="cta" field="button_text" label="Button Text" />
        <Field section="cta" field="note" label="Note Under Button" />
      </div>
    </div>
  );

  const toggleUserStatus = async (user) => {
    const newStatus = user.status === "active" ? "inactive" : "active";
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}/status`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, status: newStatus } : u));
      showToast(`${user.studio_name || user.username} marked ${newStatus}`);
    } catch {
      showToast("Failed to update status", true);
    }
  };

  const saveUser = async () => {
    if (!editingUser) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${editingUser.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(editingUser),
      });
      if (!res.ok) throw new Error();
      showToast("User updated successfully");
      setEditingUser(null);
      loadUsers();
    } catch {
      showToast("Failed to update user", true);
    }
  };

  const handleCreditWallet = async () => {
    if (!creditModal || !creditForm.amount || parseFloat(creditForm.amount) <= 0) {
      showToast("Enter a valid amount", true); return;
    }
    if (!creditForm.payment_method) {
      showToast("Select a payment method", true); return;
    }
    setCrediting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/wallet/credit`, {
        method: "POST", headers,
        body: JSON.stringify({ user_id: creditModal.id, amount: parseFloat(creditForm.amount), ...creditForm }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      showToast(`₹${creditForm.amount} credited to ${creditModal.studio_name || creditModal.username}. New balance: ₹${data.balance.toFixed(2)}`);
      setCreditModal(null);
      setCreditForm({ amount: "", payment_method: "", transaction_ref: "", description: "" });
      loadWallets();
    } catch {
      showToast("Failed to credit wallet", true);
    } finally {
      setCrediting(false);
    }
  };

  const handleAssignSubscription = async () => {
    if (!subModal || !subForm.plan_slug) { showToast("Please select a plan", true); return; }
    setAssigningSub(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${subModal.id}/subscription`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          plan_slug: subForm.plan_slug,
          duration_days: subForm.duration_days !== "" ? parseInt(subForm.duration_days) : null,
          note: subForm.note,
        }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.message || "Failed to assign plan", true); return; }
      showToast(`${subForm.plan_slug} plan assigned to ${subModal.studio_name || subModal.username}`);
      setSubModal(null);
      loadWallets();
    } catch {
      showToast("Failed to assign plan", true);
    } finally {
      setAssigningSub(false);
    }
  };

  const viewWalletTx = async (user) => {
    setWalletTxModal(user);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/wallet/${user.id}/transactions`, { headers });
      if (res.ok) setWalletTxList(await res.json());
    } catch {
      setWalletTxList([]);
    }
  };

  const saveEventCost = async () => {
    setSavingCost(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        method: "PUT", headers,
        body: JSON.stringify({ key: "event_creation_cost", value: eventCost }),
      });
      if (!res.ok) throw new Error();
      showToast(`Event creation cost updated to ₹${eventCost}`);
    } catch {
      showToast("Failed to update cost", true);
    } finally {
      setSavingCost(false);
    }
  };

  const saveQrPublicMode = async () => {
    setSavingQrMode(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        method: "PUT", headers,
        body: JSON.stringify({ key: "qr_public_mode", value: qrPublicMode }),
      });
      if (!res.ok) throw new Error();
      showToast(`QR public mode updated to ${qrPublicMode === "whatsapp" ? "WhatsApp" : "Live"}`);
    } catch {
      showToast("Failed to update QR public mode", true);
    } finally {
      setSavingQrMode(false);
    }
  };

  const renderWallet = () => {
    const filtered = walletUsers.filter((u) => {
      const q = walletSearch.toLowerCase();
      return !q || (u.studio_name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.username || "").toLowerCase().includes(q);
    });
    const totalBalance = walletUsers.reduce((s, u) => s + parseFloat(u.balance || 0), 0);

    return (
      <>
        {/* Settings */}
        <div className="admin-section" style={{ marginBottom: 20 }}>
          <div className="admin-section__header">
            <h3 className="admin-section__title">Wallet Settings</h3>
          </div>
          <div className="admin-section__body">
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
              <div className="admin-form-group" style={{ margin: 0, flex: "0 0 200px" }}>
                <label>Event Creation Cost (₹)</label>
                <input type="number" value={eventCost} onChange={(e) => setEventCost(e.target.value)} />
              </div>
              <button className="admin-btn admin-btn--primary" onClick={saveEventCost} disabled={savingCost} style={{ height: 40 }}>
                {savingCost ? "Saving..." : "Update Cost"}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginTop: 14 }}>
              <div className="admin-form-group" style={{ margin: 0, flex: "0 0 220px" }}>
                <label>QR Public Page Mode</label>
                <select value={qrPublicMode} onChange={(e) => setQrPublicMode(e.target.value)}>
                  <option value="live">Live Preview (existing)</option>
                  <option value="whatsapp">WhatsApp Preview (form view)</option>
                </select>
              </div>
              <button className="admin-btn admin-btn--primary" onClick={saveQrPublicMode} disabled={savingQrMode} style={{ height: 40 }}>
                {savingQrMode ? "Saving..." : "Update QR Mode"}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="admin-stats" style={{ marginBottom: 20 }}>
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">Total Studios</div>
            <div className="admin-stat-card__value">{walletUsers.length}</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-card__label">Total Balance (All Wallets)</div>
            <div className="admin-stat-card__value">₹{totalBalance.toFixed(2)}</div>
          </div>
        </div>

        {/* Search */}
        <div className="admin-studios-toolbar">
          <input type="text" className="admin-studios-search" placeholder="Search studios..." value={walletSearch} onChange={(e) => setWalletSearch(e.target.value)} />
          <span className="admin-studios-count">{filtered.length} studios</span>
        </div>

        {/* Table */}
        <div className="admin-section">
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Studio / Username</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th className="admin-table__right">Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: "center", color: "#9ca3af", padding: 32 }}>No studios found</td></tr>
                ) : filtered.map((u) => {
                  const subExpiry = u.subscription_purchased_at && u.duration_days
                    ? new Date(new Date(u.subscription_purchased_at).getTime() + u.duration_days * 86400000)
                    : null;
                  const isExpired = subExpiry && subExpiry < new Date();
                  const planLabel = u.subscription_type || "free";
                  return (
                  <tr key={u.id}>
                    <td className="admin-table__id">#{u.id}</td>
                    <td>
                      <div className="admin-table__studio">{u.studio_name || "—"}</div>
                      <div className="admin-table__sub">@{u.username}</div>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`admin-status admin-status--${u.status || "active"}`}>{u.status || "active"}</span>
                    </td>
                    <td>
                      <span style={{
                        display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
                        background: isExpired ? "#fee2e2" : planLabel === "free" ? "#f3f4f6" : "#dbeafe",
                        color: isExpired ? "#dc2626" : planLabel === "free" ? "#6b7280" : "#1d4ed8",
                      }}>
                        {planLabel}{isExpired ? " (expired)" : ""}
                      </span>
                      {subExpiry && !isExpired && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                          until {subExpiry.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </div>
                      )}
                    </td>
                    <td className="admin-table__right" style={{ fontWeight: 700, fontSize: 15 }}>₹{parseFloat(u.balance || 0).toFixed(2)}</td>
                    <td className="admin-table__actions">
                      <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => { setCreditModal(u); setCreditForm({ amount: "", payment_method: "", transaction_ref: "", description: "" }); }}>
                        + Credit
                      </button>
                      <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => { setSubModal(u); setSubForm({ plan_slug: u.subscription_type || "free", duration_days: "", note: "" }); }}>
                        Plan
                      </button>
                      <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => viewWalletTx(u)}>
                        History
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Credit Modal */}
        {creditModal && (
          <div className="admin-modal-overlay" onClick={() => setCreditModal(null)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal__header">
                <h3>Credit Wallet — {creditModal.studio_name || creditModal.username}</h3>
                <button className="admin-btn admin-btn--ghost" onClick={() => setCreditModal(null)}>✕</button>
              </div>
              <div className="admin-modal__body">
                <div className="admin-form-group">
                  <label>Current Balance</label>
                  <input type="text" value={`₹${parseFloat(creditModal.balance || 0).toFixed(2)}`} readOnly style={{ background: "#f3f4f6" }} />
                </div>
                <div className="admin-form-group">
                  <label>Amount to Credit (₹) *</label>
                  <input type="number" min="1" value={creditForm.amount} onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })} placeholder="e.g., 5000" />
                </div>
                <div className="admin-form-group">
                  <label>Transaction Type *</label>
                  <select className="admin-select" value={creditForm.payment_method} onChange={(e) => setCreditForm({ ...creditForm, payment_method: e.target.value })}>
                    <option value="">Select type...</option>
                    <option value="Cash">Cash</option>
                    <option value="Account Transfer">Account Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Transaction ID / Reference</label>
                  <input type="text" value={creditForm.transaction_ref} onChange={(e) => setCreditForm({ ...creditForm, transaction_ref: e.target.value })} placeholder="e.g., UTR number, receipt #" />
                </div>
                <div className="admin-form-group">
                  <label>Description</label>
                  <input type="text" value={creditForm.description} onChange={(e) => setCreditForm({ ...creditForm, description: e.target.value })} placeholder="e.g., Wallet top-up" />
                </div>
              </div>
              <div className="admin-modal__footer">
                <button className="admin-btn admin-btn--outline" onClick={() => setCreditModal(null)}>Cancel</button>
                <button className="admin-btn admin-btn--primary" onClick={handleCreditWallet} disabled={crediting}>
                  {crediting ? "Crediting..." : `Credit ₹${creditForm.amount || "0"}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transaction History Modal */}
        {walletTxModal && (
          <div className="admin-modal-overlay" onClick={() => setWalletTxModal(null)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
              <div className="admin-modal__header">
                <h3>Transactions — {walletTxModal.studio_name || walletTxModal.username}</h3>
                <button className="admin-btn admin-btn--ghost" onClick={() => setWalletTxModal(null)}>✕</button>
              </div>
              <div className="admin-modal__body" style={{ maxHeight: 400, overflowY: "auto" }}>
                {walletTxList.length === 0 ? (
                  <p style={{ textAlign: "center", color: "#9ca3af", padding: 32 }}>No transactions yet</p>
                ) : (
                  <table className="admin-table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Method</th>
                        <th>Ref</th>
                        <th style={{ textAlign: "right" }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletTxList.map((tx) => (
                        <tr key={tx.id}>
                          <td style={{ whiteSpace: "nowrap" }}>{new Date(tx.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                          <td>{tx.description || "—"}</td>
                          <td>
                            <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: tx.type === "credit" ? "#d1fae5" : "#fee2e2", color: tx.type === "credit" ? "#059669" : "#dc2626" }}>
                              {tx.type}
                            </span>
                          </td>
                          <td>{tx.payment_method || "—"}</td>
                          <td style={{ fontFamily: "monospace", fontSize: 12 }}>{tx.transaction_ref || "—"}</td>
                          <td style={{ textAlign: "right", fontWeight: 700, color: tx.type === "credit" ? "#059669" : "#dc2626" }}>
                            {tx.type === "credit" ? "+" : "−"}₹{parseFloat(tx.amount).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="admin-modal__footer">
                <button className="admin-btn admin-btn--outline" onClick={() => setWalletTxModal(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Subscription Modal */}
        {subModal && (
          <div className="admin-modal-overlay" onClick={() => setSubModal(null)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal__header">
                <h3>Change Plan — {subModal.studio_name || subModal.username}</h3>
                <button className="admin-btn admin-btn--ghost" onClick={() => setSubModal(null)}>✕</button>
              </div>
              <div className="admin-modal__body">
                <div className="admin-form-group">
                  <label>Current Plan</label>
                  <input type="text" value={subModal.subscription_type || "free"} readOnly style={{ background: "#f3f4f6" }} />
                </div>
                <div className="admin-form-group">
                  <label>Assign Plan *</label>
                  <select className="admin-select" value={subForm.plan_slug} onChange={(e) => setSubForm({ ...subForm, plan_slug: e.target.value })}>
                    <option value="">Select plan...</option>
                    {plans.map((p) => (
                      <option key={p.slug} value={p.slug}>{p.name} {p.price > 0 ? `— ₹${parseFloat(p.price).toFixed(0)}` : "(Free)"}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Duration Override (days) <span style={{ color: "#9ca3af", fontWeight: 400 }}>— leave blank to use plan default</span></label>
                  <input type="number" min="1" value={subForm.duration_days} onChange={(e) => setSubForm({ ...subForm, duration_days: e.target.value })} placeholder="e.g., 30" />
                </div>
                <div className="admin-form-group">
                  <label>Internal Note</label>
                  <input type="text" value={subForm.note} onChange={(e) => setSubForm({ ...subForm, note: e.target.value })} placeholder="e.g., Complimentary upgrade, partnership deal" />
                </div>
                <p style={{ fontSize: 12, color: "#6b7280", margin: "8px 0 0" }}>
                  This bypasses the payment gateway — the plan is granted immediately at no charge. A record is logged in the studio's wallet history.
                </p>
              </div>
              <div className="admin-modal__footer">
                <button className="admin-btn admin-btn--outline" onClick={() => setSubModal(null)}>Cancel</button>
                <button className="admin-btn admin-btn--primary" onClick={handleAssignSubscription} disabled={assigningSub || !subForm.plan_slug}>
                  {assigningSub ? "Assigning..." : "Assign Plan"}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderStudios = () => {
    const filtered = users.filter((u) => {
      const q = userSearch.toLowerCase();
      return !q || (u.studio_name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.username || "").toLowerCase().includes(q);
    });

    return (
      <>
        <div className="admin-studios-toolbar">
          <input
            type="text"
            className="admin-studios-search"
            placeholder="Search studios by name, email, or username..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />
          <span className="admin-studios-count">{filtered.length} studios</span>
        </div>

        <div className="admin-section">
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Studio / Username</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Events</th>
                  <th>Images</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: "center", color: "#9ca3af", padding: 32 }}>No studios found</td></tr>
                ) : filtered.map((u) => (
                  <tr key={u.id}>
                    <td className="admin-table__id">#{u.id}</td>
                    <td>
                      <div className="admin-table__studio">{u.studio_name || "—"}</div>
                      <div className="admin-table__sub">@{u.username}</div>
                    </td>
                    <td>{u.email}</td>
                    <td>{u.phone_number || "—"}</td>
                    <td>
                      <span className={`admin-status admin-status--${u.status || "active"}`}>
                        {u.status || "active"}
                      </span>
                    </td>
                    <td>{u.event_count}</td>
                    <td>{u.image_count}</td>
                    <td className="admin-table__date">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="admin-table__actions">
                      <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => setEditingUser({ ...u })}>
                        Edit
                      </button>
                      <button
                        className={`admin-btn admin-btn--sm ${u.status === "active" ? "admin-btn--danger" : "admin-btn--primary"}`}
                        onClick={() => toggleUserStatus(u)}
                      >
                        {u.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="admin-btn admin-btn--sm admin-btn--login-as"
                        onClick={() => handleImpersonate(u)}
                        title={`Login as ${u.studio_name || u.username}`}
                      >
                        🔑 Login As
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Modal */}
        {editingUser && (
          <div className="admin-modal-overlay" onClick={() => setEditingUser(null)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal__header">
                <h3>Edit Studio — #{editingUser.id}</h3>
                <button className="admin-btn admin-btn--ghost" onClick={() => setEditingUser(null)}>✕</button>
              </div>
              <div className="admin-modal__body">
                {[
                  { key: "studio_name", label: "Studio Name" },
                  { key: "username", label: "Username" },
                  { key: "email", label: "Email" },
                  { key: "phone_number", label: "Phone" },
                  { key: "address", label: "Address" },
                  { key: "city", label: "City" },
                  { key: "state", label: "State" },
                  { key: "country", label: "Country" },
                  { key: "postal_code", label: "Postal Code" },
                ].map((f) => (
                  <div className="admin-form-group" key={f.key}>
                    <label>{f.label}</label>
                    <input
                      type="text"
                      value={editingUser[f.key] || ""}
                      onChange={(e) => setEditingUser({ ...editingUser, [f.key]: e.target.value })}
                    />
                  </div>
                ))}
                <div className="admin-form-group">
                  <label>Bio</label>
                  <textarea
                    value={editingUser.bio || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, bio: e.target.value })}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Status</label>
                  <select
                    value={editingUser.status || "active"}
                    onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                    className="admin-select"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Role</label>
                  <select
                    value={editingUser.role || "user"}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="admin-select"
                  >
                    <option value="user">User</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal__footer">
                <button className="admin-btn admin-btn--outline" onClick={() => setEditingUser(null)}>Cancel</button>
                <button className="admin-btn admin-btn--primary" onClick={saveUser}>Save Changes</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderBanners = () => {
    const filteredEvents = bannerEvents.filter(
      (ev) =>
        ev.event_name.toLowerCase().includes(bannerEventSearch.toLowerCase()) ||
        (ev.studio_name || "").toLowerCase().includes(bannerEventSearch.toLowerCase())
    );

    return (
      <div className="admin-section">
        <div className="admin-section__header">
          <h3 className="admin-section__title">Advertisement Banners</h3>
        </div>
        <div className="admin-section__body">
          <p style={{ color: "#6b7280", marginBottom: 16, fontSize: 14 }}>
            Upload wide banner images for each event. These auto-slide at the top of the client shared gallery page.
          </p>

          {/* Event selector */}
          <div style={{ marginBottom: 20 }}>
            <input
              type="text"
              placeholder="Search events..."
              value={bannerEventSearch}
              onChange={(e) => setBannerEventSearch(e.target.value)}
              className="admin-input"
              style={{ marginBottom: 12, maxWidth: 400 }}
            />
            <div className="admin-banner-events">
              {filteredEvents.map((ev) => (
                <div
                  key={ev.id}
                  className={`admin-banner-event-card${selectedBannerEventId === ev.id ? " active" : ""}`}
                  onClick={() => handleSelectBannerEvent(ev.id)}
                >
                  <div className="admin-banner-event-name">{ev.event_name}</div>
                  <div className="admin-banner-event-meta">
                    {ev.studio_name && <span>{ev.studio_name}</span>}
                    {ev.event_date && <span> • {new Date(ev.event_date).toLocaleDateString()}</span>}
                  </div>
                  <div className="admin-banner-event-count">
                    {ev.banner_count} banner{ev.banner_count !== 1 ? "s" : ""}
                  </div>
                </div>
              ))}
              {filteredEvents.length === 0 && (
                <p style={{ color: "#9ca3af", fontSize: 14 }}>No events found</p>
              )}
            </div>
          </div>

          {/* Banner management for selected event */}
          {selectedBannerEventId && (
            <div className="admin-banner-manage">
              <div className="admin-banner-manage-header">
                <h4 style={{ margin: 0, fontSize: 16, color: "#1e1b4b" }}>
                  Banners for: {bannerEvents.find((e) => e.id === selectedBannerEventId)?.event_name}
                </h4>
                <label className="admin-btn admin-btn--primary" style={{ cursor: "pointer", fontSize: 13 }}>
                  {bannerUploading ? "⏳ Uploading..." : "➕ Add Banner"}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleBannerUpload}
                    disabled={bannerUploading}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
              <div style={{ margin: "6px 0 14px" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#7c3aed", background: "#f5f3ff", padding: "4px 10px", borderRadius: 6, border: "1px solid #e0e7ff" }}>📐 Recommended: 1200 × 240 px</span>
                <p style={{ color: "#9ca3af", fontSize: 11, margin: "4px 0 0" }}>Wide 5:1 strip fits the shared-gallery slider best. Any size works — banners are always shown in full, never cropped.</p>
              </div>
              {banners.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>
                  No banners yet. Click "Add Banner" to upload.
                </div>
              ) : (
                <div className="admin-banner-grid">
                  {banners.map((banner, idx) => (
                    <div key={banner.id} className="admin-banner-card">
                      <div
                        className="admin-banner-bg"
                        style={{ backgroundImage: `url(${banner.image_url})` }}
                        aria-hidden="true"
                      />
                      <img src={banner.image_url} alt={`Banner ${idx + 1}`} className="admin-banner-img" />
                      <div className="admin-banner-overlay">
                        <span className="admin-banner-order">#{idx + 1}</span>
                        <button
                          className="admin-banner-del"
                          onClick={(e) => { e.stopPropagation(); handleDeleteBanner(banner.id); }}
                        >✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const AI_MODELS = [
    { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash", desc: "Most capable, best quality responses" },
    { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite", desc: "Fast & lightweight, good balance" },
    { value: "gemini-2.0-flash", label: "Gemini 2.0 Flash", desc: "Previous gen, stable performance" },
    { value: "gemini-2.0-flash-lite", label: "Gemini 2.0 Flash Lite", desc: "Previous gen lightweight, fastest" },
  ];

  const saveAiModel = async (model) => {
    setAiModel(model);
    setSavingAiModel(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        method: "PUT", headers,
        body: JSON.stringify({ key: "gemini_model", value: model }),
      });
      if (!res.ok) throw new Error();
      showToast(`AI model updated to ${AI_MODELS.find(m => m.value === model)?.label || model}`);
    } catch {
      showToast("Failed to update AI model", true);
    } finally {
      setSavingAiModel(false);
    }
  };

  const renderAiSettings = () => (
    <>
      <div className="admin-section">
        <div className="admin-section__header">
          <h3 className="admin-section__title">🤖 Gemini AI Model</h3>
        </div>
        <div className="admin-section__body">
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Choose which Gemini model all studios will use for Photentra AI. The selected model is the primary choice — if it hits rate limits, others are used as fallbacks automatically.
          </p>
          <div className="admin-ai-models">
            {AI_MODELS.map(m => (
              <div
                key={m.value}
                className={`admin-ai-model-card${aiModel === m.value ? " admin-ai-model-card--active" : ""}`}
                onClick={() => !savingAiModel && saveAiModel(m.value)}
              >
                <div className="admin-ai-model-card__radio">
                  <div className={`admin-ai-model-card__dot${aiModel === m.value ? " admin-ai-model-card__dot--active" : ""}`} />
                </div>
                <div className="admin-ai-model-card__info">
                  <div className="admin-ai-model-card__name">{m.label}</div>
                  <div className="admin-ai-model-card__desc">{m.desc}</div>
                  <div className="admin-ai-model-card__id">{m.value}</div>
                </div>
                {aiModel === m.value && <span className="admin-ai-model-card__badge">Active</span>}
              </div>
            ))}
          </div>
          {savingAiModel && <p style={{ color: "#764ba2", marginTop: 12, fontSize: 13 }}>Saving...</p>}
        </div>
      </div>
    </>
  );

  const renderPlans = () => (
    <>
      <div className="admin-section" style={{ marginBottom: 20 }}>
        <div className="admin-section__header">
          <h3 className="admin-section__title">🎟️ Subscription Plans</h3>
        </div>
        <div className="admin-section__body">
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Price, event limit, and photo limit apply the moment a studio buys or is on this plan — enforced live on
            event creation and photo upload. "Uncompressed" plans store and serve photos at full original quality
            everywhere in the portal, with no resize/quality step applied.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {plans.map((p) => {
          const edit = planEdits[p.slug] || {};
          return (
            <div className="admin-section" key={p.slug}>
              <div className="admin-section__header">
                <h3 className="admin-section__title">{p.name}</h3>
              </div>
              <div className="admin-section__body">
                <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 160px" }}>
                    <label>Monthly Price (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={edit.price ?? ""}
                      onChange={(e) => updatePlanEdit(p.slug, "price", e.target.value)}
                    />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 160px" }}>
                    <label>Yearly Price (₹)</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="No yearly option"
                      value={edit.price_yearly ?? ""}
                      onChange={(e) => updatePlanEdit(p.slug, "price_yearly", e.target.value)}
                    />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 200px" }}>
                    <label>Event Limit</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Unlimited"
                      value={edit.max_events ?? ""}
                      onChange={(e) => updatePlanEdit(p.slug, "max_events", e.target.value)}
                    />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 220px" }}>
                    <label>Photos per Event</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Unlimited"
                      value={edit.max_photos_per_event ?? ""}
                      onChange={(e) => updatePlanEdit(p.slug, "max_photos_per_event", e.target.value)}
                    />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 200px" }}>
                    <label>Storage Limit (GB)</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="No storage limit"
                      value={edit.max_storage_gb ?? ""}
                      onChange={(e) => updatePlanEdit(p.slug, "max_storage_gb", e.target.value)}
                    />
                    {edit.max_storage_gb && parseFloat(edit.max_storage_gb) >= 1024 && (
                      <span style={{ fontSize: 12, color: "#6b7280" }}>= {(parseFloat(edit.max_storage_gb) / 1024).toFixed(0)} TB</span>
                    )}
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 240px" }}>
                    <label>Photo Quality on Upload</label>
                    <select
                      value={edit.no_compression ? "uncompressed" : "compressed"}
                      onChange={(e) => updatePlanEdit(p.slug, "no_compression", e.target.value === "uncompressed")}
                    >
                      <option value="compressed">Compressed</option>
                      <option value="uncompressed">Uncompressed (original quality)</option>
                    </select>
                  </div>
                  {p.slug !== "free" && (
                    <div className="admin-form-group" style={{ margin: 0, flex: "0 0 180px" }}>
                      <label>Duration (days)</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="Never expires"
                        value={edit.duration_days ?? ""}
                        onChange={(e) => updatePlanEdit(p.slug, "duration_days", e.target.value)}
                      />
                      {formatDuration(edit.duration_days) && (
                        <span style={{ fontSize: 12, color: "#6b7280" }}>= {formatDuration(edit.duration_days)}</span>
                      )}
                    </div>
                  )}
                  <button
                    className="admin-btn admin-btn--primary"
                    onClick={() => savePlan(p.slug)}
                    disabled={savingPlan === p.slug}
                    style={{ height: 40 }}
                  >
                    {savingPlan === p.slug ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {plans.length === 0 && <p style={{ color: "#666" }}>Loading plans...</p>}
      </div>
    </>
  );

  const renderPhotoFrames = () => (
    <>
      <div className="admin-section" style={{ marginBottom: 20 }}>
        <div className="admin-section__header">
          <h3 className="admin-section__title">Platform Markup</h3>
        </div>
        <div className="admin-section__body">
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Added on top of whatever price a studio sets per frame size — the guest pays studioPrice + markup, the
            studio's payout stays their full listed price, and this markup is the platform's revenue.
          </p>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
            <div className="admin-form-group" style={{ margin: 0, flex: "0 0 200px" }}>
              <label>Markup (%)</label>
              <input type="number" min="0" value={frameMarkup} onChange={(e) => setFrameMarkup(e.target.value)} />
            </div>
            <button className="admin-btn admin-btn--primary" onClick={saveFrameMarkup} disabled={savingFrameMarkup} style={{ height: 40 }}>
              {savingFrameMarkup ? "Saving..." : "Update Markup"}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-section" style={{ marginBottom: 20 }}>
        <div className="admin-section__header">
          <h3 className="admin-section__title">🖼️ Frame Dimensions</h3>
        </div>
        <div className="admin-section__body">
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            The fixed catalog of sizes every studio prices individually in their own Photo Frames settings. Deactivate
            a size instead of deleting it once it's been priced or ordered.
          </p>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            <div className="admin-form-group" style={{ margin: 0, flex: "0 0 140px" }}>
              <label>Label</label>
              <input type="text" placeholder="8x10" value={newDimension.label} onChange={(e) => setNewDimension((p) => ({ ...p, label: e.target.value }))} />
            </div>
            <div className="admin-form-group" style={{ margin: 0, flex: "0 0 120px" }}>
              <label>Width (in)</label>
              <input type="number" min="0" step="0.1" value={newDimension.width_inches} onChange={(e) => setNewDimension((p) => ({ ...p, width_inches: e.target.value }))} />
            </div>
            <div className="admin-form-group" style={{ margin: 0, flex: "0 0 120px" }}>
              <label>Height (in)</label>
              <input type="number" min="0" step="0.1" value={newDimension.height_inches} onChange={(e) => setNewDimension((p) => ({ ...p, height_inches: e.target.value }))} />
            </div>
            <button className="admin-btn admin-btn--primary" onClick={addDimension} disabled={addingDimension} style={{ height: 40 }}>
              {addingDimension ? "Adding..." : "Add Dimension"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {frameDimensions.map((d) => {
          const edit = dimensionEdits[d.id] || {};
          return (
            <div className="admin-section" key={d.id}>
              <div className="admin-section__body">
                <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 140px" }}>
                    <label>Label</label>
                    <input type="text" value={edit.label ?? ""} onChange={(e) => updateDimensionEdit(d.id, "label", e.target.value)} />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 120px" }}>
                    <label>Width (in)</label>
                    <input type="number" min="0" step="0.1" value={edit.width_inches ?? ""} onChange={(e) => updateDimensionEdit(d.id, "width_inches", e.target.value)} />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 120px" }}>
                    <label>Height (in)</label>
                    <input type="number" min="0" step="0.1" value={edit.height_inches ?? ""} onChange={(e) => updateDimensionEdit(d.id, "height_inches", e.target.value)} />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 100px" }}>
                    <label>Order</label>
                    <input type="number" value={edit.display_order ?? 0} onChange={(e) => updateDimensionEdit(d.id, "display_order", e.target.value)} />
                  </div>
                  <div className="admin-form-group" style={{ margin: 0, flex: "0 0 140px" }}>
                    <label>Status</label>
                    <select value={edit.active ? "active" : "inactive"} onChange={(e) => updateDimensionEdit(d.id, "active", e.target.value === "active")}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <button className="admin-btn admin-btn--primary" onClick={() => saveDimension(d.id)} disabled={savingDimension === d.id} style={{ height: 40 }}>
                    {savingDimension === d.id ? "Saving..." : "Save"}
                  </button>
                  <button className="admin-btn admin-btn--danger" onClick={() => deleteDimension(d.id)} style={{ height: 40 }}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {frameDimensions.length === 0 && <p style={{ color: "#666" }}>No frame dimensions yet.</p>}
      </div>
    </>
  );

  const ORDER_STATUS_LABELS = { awaiting_payment: "Awaiting Payment", paid: "Paid", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };
  const PAYOUT_STATUS_LABELS = { not_started: "Not Started", in_progress: "In Progress", paid: "Paid" };

  const renderFramePayouts = () => (
    <>
      <div className="admin-section" style={{ marginBottom: 20 }}>
        <div className="admin-section__header">
          <h3 className="admin-section__title">📦 Photo Frame Orders</h3>
        </div>
        <div className="admin-section__body">
          <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
            Full cross-studio order detail. Payouts to studios happen offline (bank transfer, etc) — amounts owed
            are the studio's frame price plus their shipping cost, never the platform markup. Set the payout status
            per order once you've paid; studios see this same status on their own Orders page.
          </p>
          {frameOrderSummary.byStudio.length === 0 ? (
            <p style={{ color: "#666" }}>Nothing owed right now.</p>
          ) : (
            <>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Total owed (delivered, not yet paid): ₹{frameOrderSummary.totalOwed.toFixed(2)}</div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Studio</th>
                      <th>Orders owed</th>
                      <th className="admin-table__right">Owed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {frameOrderSummary.byStudio.map((s) => (
                      <tr key={s.studio_id}>
                        <td>{s.studio_name}</td>
                        <td>{s.count}</td>
                        <td className="admin-table__right">₹{parseFloat(s.total_owed).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="admin-section">
        <div className="admin-section__header">
          <h3 className="admin-section__title">All Orders</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <select value={frameOrderStatusFilter} onChange={(e) => setFrameOrderStatusFilter(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6 }}>
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
            </select>
            <select value={frameOrderPayoutFilter} onChange={(e) => setFrameOrderPayoutFilter(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6 }}>
              <option value="">All payout statuses</option>
              <option value="not_started">Payout: Not Started</option>
              <option value="in_progress">Payout: In Progress</option>
              <option value="paid">Payout: Paid</option>
            </select>
          </div>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Transaction ID</th>
                <th>File Name</th>
                <th>Event</th>
                <th>Studio</th>
                <th>Guest Name</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Frame</th>
                <th className="admin-table__right">Owed</th>
                <th>Status</th>
                <th>Payout Status</th>
              </tr>
            </thead>
            <tbody>
              {frameOrderItems.length === 0 ? (
                <tr><td colSpan="12" style={{ textAlign: "center", color: "#9ca3af", padding: 32 }}>No orders</td></tr>
              ) : frameOrderItems.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>{o.order_number}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>{o.gateway_txn_id || "—"}</td>
                  <td>{o.original_filename || "—"}</td>
                  <td>{o.gallery_name}</td>
                  <td>
                    <button
                      onClick={() => goToStudioProfile(o.studio_email)}
                      style={{ background: "none", border: "none", color: "#7c3aed", fontWeight: 600, cursor: "pointer", padding: 0, textAlign: "left" }}
                    >
                      {o.studio_name}
                    </button>
                    <br /><span style={{ fontSize: 12, color: "#9ca3af" }}>{o.studio_email}</span>
                  </td>
                  <td>{o.guest_name}</td>
                  <td>{o.guest_phone}{o.guest_email ? <><br /><span style={{ fontSize: 12, color: "#9ca3af" }}>{o.guest_email}</span></> : null}</td>
                  <td style={{ fontSize: 12, maxWidth: 200 }}>
                    {o.shipping_address_line1}{o.shipping_address_line2 ? `, ${o.shipping_address_line2}` : ""}, {o.shipping_city}, {o.shipping_state} {o.shipping_postal_code}, {o.shipping_country}
                  </td>
                  <td>{o.frame_label}{(o.status === "shipped" || o.status === "delivered") && o.carrier_name && (
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{o.carrier_name} · AWB {o.tracking_number}</div>
                  )}</td>
                  <td className="admin-table__right">
                    ₹{(parseFloat(o.studio_price) + parseFloat(o.shipping_cost || 0)).toFixed(2)}
                    {parseFloat(o.shipping_cost) > 0 && (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>frame ₹{parseFloat(o.studio_price).toFixed(2)} + shipping ₹{parseFloat(o.shipping_cost).toFixed(2)}</div>
                    )}
                  </td>
                  <td>
                    <div>{ORDER_STATUS_LABELS[o.status] || o.status}</div>
                    {o.status === "shipped" && (
                      <button className="admin-btn admin-btn--sm admin-btn--primary" style={{ marginTop: 6 }} onClick={() => deliverFrameOrder(o)} disabled={deliveringOrderId === o.id}>
                        {deliveringOrderId === o.id ? "Saving..." : "Mark Delivered"}
                      </button>
                    )}
                  </td>
                  <td>
                    <select
                      value={o.payout_status}
                      onChange={(e) => updatePayoutStatus(o.id, e.target.value)}
                      disabled={savingPayoutStatusId === o.id}
                      style={{ padding: "5px 8px", borderRadius: 6, fontSize: 12 }}
                    >
                      {Object.entries(PAYOUT_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const fmt = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);

  const renderPaymentLinks = () => {
    const createLink = async (e) => {
      e.preventDefault();
      if (!paymentLinkForm.amount || !paymentLinkForm.description.trim()) return;
      setCreatingPaymentLink(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/payment-links`, {
          method: "POST", headers,
          body: JSON.stringify(paymentLinkForm),
        });
        const data = await res.json();
        if (!res.ok) { showToast(data.message || "Failed to create link", true); return; }
        setPaymentLinks([data, ...paymentLinks]);
        setPaymentLinkForm({ amount: "", description: "", note: "" });
        showToast("Payment link created");
      } catch {
        showToast("Error creating link", true);
      } finally {
        setCreatingPaymentLink(false);
      }
    };

    const deleteLink = async (id) => {
      if (!window.confirm("Delete this payment link?")) return;
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/payment-links/${id}`, { method: "DELETE", headers });
        const data = await res.json();
        if (!res.ok) { showToast(data.message || "Cannot delete", true); return; }
        setPaymentLinks(paymentLinks.filter((l) => l.id !== id));
        showToast("Deleted");
      } catch {
        showToast("Error deleting link", true);
      }
    };

    const copyLink = (linkToken) => {
      navigator.clipboard.writeText(`${publicOrigin()}/pay/${linkToken}`);
      showToast("Link copied to clipboard");
    };

    const statusBadge = (status) => {
      const colors = { active: "#166534", paid: "#1e40af" };
      const bgs = { active: "#dcfce7", paid: "#dbeafe" };
      return (
        <span style={{
          background: bgs[status] || "#f3f4f6", color: colors[status] || "#374151",
          borderRadius: 99, padding: "2px 10px", fontSize: 12, fontWeight: 600,
        }}>{status}</span>
      );
    };

    return (
      <>
        <div className="admin-section">
          <div className="admin-section__header">
            <h3 className="admin-section__title">Create Payment Link</h3>
          </div>
          <div className="admin-section__body">
            <form onSubmit={createLink} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                  Amount (₹)
                </label>
                <input
                  className="admin-input"
                  type="number" min="1" step="0.01"
                  placeholder="e.g. 5000"
                  value={paymentLinkForm.amount}
                  onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                  Description (shown to payer)
                </label>
                <input
                  className="admin-input"
                  type="text"
                  placeholder="e.g. Wedding photography booking"
                  value={paymentLinkForm.description}
                  onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, description: e.target.value })}
                  required
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                  Note (optional, shown below description)
                </label>
                <textarea
                  className="admin-input"
                  rows={2}
                  placeholder="Any extra info for the payer"
                  value={paymentLinkForm.note}
                  onChange={(e) => setPaymentLinkForm({ ...paymentLinkForm, note: e.target.value })}
                  style={{ resize: "vertical" }}
                />
              </div>
              <button className="admin-btn admin-btn--primary" type="submit" disabled={creatingPaymentLink}>
                {creatingPaymentLink ? "Creating…" : "Create Link"}
              </button>
            </form>
          </div>
        </div>

        <div className="admin-section" style={{ marginTop: 24 }}>
          <div className="admin-section__header">
            <h3 className="admin-section__title">All Payment Links</h3>
          </div>
          <div className="admin-section__body" style={{ overflowX: "auto" }}>
            {paymentLinks.length === 0 ? (
              <p style={{ color: "#6b7280" }}>No payment links yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                    <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280" }}>Description</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280" }}>Amount</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280" }}>Status</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280" }}>Payer</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280" }}>Paid At</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280" }}>Created</th>
                    <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentLinks.map((l) => (
                    <tr key={l.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 600 }}>{l.description}</div>
                        {l.note && <div style={{ color: "#9ca3af", fontSize: 12 }}>{l.note}</div>}
                      </td>
                      <td style={{ padding: "10px 12px", fontWeight: 700, color: "#1e1b4b" }}>{fmt(l.amount)}</td>
                      <td style={{ padding: "10px 12px" }}>{statusBadge(l.status)}</td>
                      <td style={{ padding: "10px 12px", color: "#374151" }}>
                        {l.payer_name ? (
                          <>
                            <div>{l.payer_name}</div>
                            <div style={{ color: "#9ca3af", fontSize: 12 }}>{l.payer_email}</div>
                            <div style={{ color: "#9ca3af", fontSize: 12 }}>{l.payer_phone}</div>
                          </>
                        ) : <span style={{ color: "#d1d5db" }}>—</span>}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#374151" }}>
                        {l.paid_at ? new Date(l.paid_at).toLocaleString("en-IN") : <span style={{ color: "#d1d5db" }}>—</span>}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#9ca3af" }}>
                        {new Date(l.created_at).toLocaleDateString("en-IN")}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button className="admin-btn admin-btn--outline" style={{ fontSize: 12, padding: "4px 10px" }}
                            onClick={() => copyLink(l.token)}>
                            Copy Link
                          </button>
                          <a
                            href={`/pay/${l.token}`} target="_blank" rel="noreferrer"
                            className="admin-btn admin-btn--outline"
                            style={{ fontSize: 12, padding: "4px 10px", textDecoration: "none" }}>
                            Open
                          </a>
                          {l.status !== "paid" && (
                            <button className="admin-btn" style={{ fontSize: 12, padding: "4px 10px", background: "#fee2e2", color: "#dc2626", border: "none" }}
                              onClick={() => deleteLink(l.id)}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </>
    );
  };

  const loadExchangeRates = async () => {
    setExchangeRatesLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/exchange-rates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) setExchangeRates(data);
    } catch {}
    setExchangeRatesLoading(false);
  };

  const handleSaveRate = async (curr, manualRate) => {
    setSavingRate(curr);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/exchange-rates/${curr}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        body: JSON.stringify({ manual_rate: manualRate === "" ? null : parseFloat(manualRate) }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(data.message || "Rate updated");
        loadExchangeRates();
      } else {
        showToast(data.message || "Failed to update rate", true);
      }
    } catch { showToast("Network error", true); }
    setSavingRate(null);
  };

  const renderExchangeRates = () => {
    if (exchangeRates.length === 0 && !exchangeRatesLoading) {
      loadExchangeRates();
    }
    return (
      <div>
        <p style={{ color: "#6b7280", marginBottom: 16, fontSize: 14 }}>
          Exchange rates are auto-refreshed every 6 hours from a live API. Set a manual override to lock a currency's rate — clear it to revert to the live rate.
        </p>
        {exchangeRatesLoading ? (
          <p>Loading rates...</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
                <th style={{ padding: "8px 12px" }}>Currency</th>
                <th style={{ padding: "8px 12px" }}>Symbol</th>
                <th style={{ padding: "8px 12px" }}>Live Rate (1 INR =)</th>
                <th style={{ padding: "8px 12px" }}>Manual Override</th>
                <th style={{ padding: "8px 12px" }}>Effective Rate</th>
                <th style={{ padding: "8px 12px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exchangeRates.filter(r => r.currency !== 'INR').map((r) => (
                <tr key={r.currency} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{r.currency} — {r.name}</td>
                  <td style={{ padding: "10px 12px" }}>{r.symbol}</td>
                  <td style={{ padding: "10px 12px", color: "#6b7280" }}>{parseFloat(r.rate).toFixed(6)}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <input
                      type="number"
                      step="0.000001"
                      placeholder="Use live rate"
                      defaultValue={r.manual_rate || ""}
                      id={`rate-input-${r.currency}`}
                      style={{ width: 120, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }}
                    />
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: r.manual_rate ? "#7c3aed" : "#059669" }}>
                    {parseFloat(r.effective_rate).toFixed(6)}
                    {r.manual_rate && <span style={{ fontSize: 10, color: "#7c3aed", marginLeft: 4 }}>MANUAL</span>}
                  </td>
                  <td style={{ padding: "10px 12px", display: "flex", gap: 6 }}>
                    <button
                      disabled={savingRate === r.currency}
                      onClick={() => {
                        const input = document.getElementById(`rate-input-${r.currency}`);
                        handleSaveRate(r.currency, input?.value || "");
                      }}
                      style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600, border: "1px solid #7c3aed", background: "#f5f3ff", color: "#7c3aed", borderRadius: 6, cursor: "pointer" }}
                    >
                      {savingRate === r.currency ? "..." : "Save"}
                    </button>
                    {r.manual_rate && (
                      <button
                        disabled={savingRate === r.currency}
                        onClick={() => handleSaveRate(r.currency, "")}
                        style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600, border: "1px solid #ef4444", background: "#fef2f2", color: "#ef4444", borderRadius: 6, cursor: "pointer" }}
                      >
                        Clear
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 16 }}>
          <button
            onClick={loadExchangeRates}
            disabled={exchangeRatesLoading}
            style={{ padding: "8px 16px", fontSize: 13, fontWeight: 600, border: "1px solid #d1d5db", background: "#fff", borderRadius: 8, cursor: "pointer" }}
          >
            {exchangeRatesLoading ? "Refreshing..." : "Refresh Rates"}
          </button>
        </div>
      </div>
    );
  };

  const sectionRenderers = {
    dashboard: renderDashboard,
    studios: renderStudios,
    wallet: renderWallet,
    plans: renderPlans,
    photo_frames: renderPhotoFrames,
    frame_payouts: renderFramePayouts,
    payment_links: renderPaymentLinks,
    hero: renderHero,
    brands: renderBrands,
    features: renderFeatures,
    album_review: renderAlbumReview,
    services: renderServices,
    steps: renderSteps,
    pricing: renderPricing,
    testimonials: renderTestimonials,
    faq: renderFaq,
    cta: renderCta,
    banners: renderBanners,
    ai: renderAiSettings,
    exchange_rates: renderExchangeRates,
  };

  return (
    <div className="admin">
      {/* Toast */}
      {toast && (
        <div className={`admin-toast${toast.isError ? " admin-toast--error" : ""}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <header className="admin-header">
        <div className="admin-header__left">
          <button
            className="admin-nav-toggle"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
          <img src={process.env.PUBLIC_URL + "/photentra-logo.png"} alt="Photentra" className="admin-header__logo" />
          <h1 className="admin-header__title">Admin Panel</h1>
          <span className="admin-header__badge">Super Admin</span>
        </div>
        <div className="admin-header__right">
          <span className="admin-header__user">{localStorage.getItem("userEmail")}</span>
          <button className="admin-btn admin-btn--outline" onClick={() => navigate("/dashboard")}>
            ← Back to App
          </button>
          <button className="admin-btn admin-btn--ghost" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar — a slide-in drawer under 768px, static column above it */}
        {mobileNavOpen && (
          <div className="admin-sidebar-backdrop" onClick={() => setMobileNavOpen(false)} />
        )}
        <aside className={`admin-sidebar${mobileNavOpen ? " admin-sidebar--open" : ""}`}>
          <div className="admin-sidebar__section">
            <div className="admin-sidebar__label">Content Management</div>
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                className={`admin-sidebar__item${activeSection === s.key ? " admin-sidebar__item--active" : ""}`}
                onClick={() => { setActiveSection(s.key); setMobileNavOpen(false); }}
              >
                <span>{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <main className="admin-main">
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1e1b4b", marginBottom: 24 }}>
            {SECTIONS.find((s) => s.key === activeSection)?.label || "Dashboard"}
          </h2>
          {sectionRenderers[activeSection]?.()}
        </main>
      </div>

      {/* Save Bar */}
      {hasChanges && (
        <div className="admin-save-bar">
          <span className="admin-save-bar__msg">You have unsaved changes</span>
          <div className="admin-save-bar__actions">
            <button className="admin-btn admin-btn--outline" onClick={loadContent}>
              Discard
            </button>
            <button className="admin-btn admin-btn--primary" onClick={saveAll} disabled={saving}>
              {saving ? "Saving..." : "Save All Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
